<script setup>
import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  sequence: { type: Array, default: () => [] },
  accountId: { type: [String, Number], default: null },
  templateOnly: { type: Boolean, default: false },
});

const emit = defineEmits(['update:sequence']);

const { t } = useI18n();
const store = useStore();
const alert = useAlert();

const activeIndex = ref(0);
const uploading = ref({});
const showVariables = ref(false);
const textareaRef = ref(null);
const templateLoading = ref(false);
const templateInboxId = ref(null);
const templateList = ref([]);
const showAdvancedAi = ref(false);
const uploadingHeaderImage = ref(false);

const whatsappInboxes = useMapGetter('inboxes/getWhatsAppInboxes');

const MESSAGE_TYPES = computed(() => [
  {
    type: 'text',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.MSG_TEXT'),
    icon: 'i-lucide-message-square',
  },
  {
    type: 'ai',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.MSG_AI'),
    icon: 'i-lucide-sparkles',
  },
  {
    type: 'template',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.MSG_TEMPLATE'),
    icon: 'i-lucide-layout-template',
  },
  {
    type: 'image',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.TYPES.IMAGE'),
    icon: 'i-lucide-image',
  },
  {
    type: 'video',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.TYPES.VIDEO'),
    icon: 'i-lucide-video',
  },
  {
    type: 'audio',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.MSG_AUDIO'),
    icon: 'i-lucide-mic',
  },
  {
    type: 'document',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.MSG_DOCUMENT'),
    icon: 'i-lucide-file-text',
  },
  {
    type: 'wait',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.MSG_WAIT'),
    icon: 'i-lucide-clock',
  },
]);

// When templateOnly is true (Cloud API / HubNavi inbox), only show template option
const availableTypes = computed(() =>
  props.templateOnly
    ? MESSAGE_TYPES.value.filter(mt => mt.type === 'template')
    : MESSAGE_TYPES.value
);

const AI_TONES = computed(() => [
  {
    value: 'friendly',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.TONE_FRIENDLY'),
    desc: t('CAMPAIGN.WIZARD.SEQUENCE.TONE_FRIENDLY_DESC'),
  },
  {
    value: 'professional',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.TONE_PROFESSIONAL'),
    desc: t('CAMPAIGN.WIZARD.SEQUENCE.TONE_PROFESSIONAL_DESC'),
  },
  {
    value: 'casual',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.TONE_CASUAL'),
    desc: t('CAMPAIGN.WIZARD.SEQUENCE.TONE_CASUAL_DESC'),
  },
  {
    value: 'persuasive',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.TONE_PERSUASIVE'),
    desc: t('CAMPAIGN.WIZARD.SEQUENCE.TONE_PERSUASIVE_DESC'),
  },
]);

const VARIABLES = computed(() => [
  {
    key: 'primeiro_nome',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.VAR_FIRST_NAME'),
    icon: 'i-lucide-user',
  },
  {
    key: 'contact.name',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.VAR_NAME'),
    icon: 'i-lucide-user-round',
  },
  {
    key: 'contact.phone',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.VAR_PHONE'),
    icon: 'i-lucide-phone',
  },
  {
    key: 'contact.email',
    label: t('CAMPAIGN.WIZARD.SEQUENCE.VAR_EMAIL'),
    icon: 'i-lucide-mail',
  },
]);

const activeMessage = computed(() => props.sequence[activeIndex.value]);

const getTypeConfig = type =>
  MESSAGE_TYPES.value.find(mt => mt.type === type) || MESSAGE_TYPES.value[0];

const getPreviewText = item => {
  if (item.type === 'text') {
    const txt = item.content?.text || '';
    return txt.length > 35
      ? `${txt.substring(0, 35)}...`
      : txt || getTypeConfig(item.type).label;
  }
  if (item.type === 'ai') {
    const prompt = item.content?.userPrompt || '';
    return prompt.length > 30
      ? `${prompt.substring(0, 30)}...`
      : prompt || t('CAMPAIGN.WIZARD.SEQUENCE.AI_LABEL');
  }
  if (item.type === 'template') {
    return item.content?.templateName || 'Template';
  }
  if (item.type === 'wait') {
    return `${t('CAMPAIGN.WIZARD.SEQUENCE.WAIT_TIME')}: ${item.content?.waitTime || 30}s`;
  }
  return item.content?.fileName || getTypeConfig(item.type).label;
};

const addMessage = type => {
  let content;
  switch (type) {
    case 'text':
      content = { text: '' };
      break;
    case 'ai':
      content = {
        systemPrompt: t('CAMPAIGN.WIZARD.SEQUENCE.AI_DEFAULT_SYSTEM'),
        userPrompt: '',
        tone: 'friendly',
        useEmojis: true,
        includeGreeting: true,
        maxLength: 500,
      };
      break;
    case 'template':
      content = {
        inboxId: null,
        templateName: '',
        templateId: null,
        templateData: null,
        variables: {},
      };
      break;
    case 'wait':
      content = { waitTime: 30 };
      break;
    case 'image':
    case 'video':
      content = { url: '', caption: '', fileName: '' };
      break;
    case 'audio':
    case 'document':
      content = { url: '', fileName: '' };
      break;
    default:
      content = { text: '' };
  }
  const newSeq = [...props.sequence, { type, content }];
  emit('update:sequence', newSeq);
  activeIndex.value = newSeq.length - 1;
};

const removeMessage = index => {
  const newSeq = props.sequence.filter((_, i) => i !== index);
  emit('update:sequence', newSeq);
  if (activeIndex.value >= newSeq.length) {
    activeIndex.value = Math.max(0, newSeq.length - 1);
  }
};

const updateMessage = (index, content) => {
  const newSeq = [...props.sequence];
  newSeq[index] = { ...newSeq[index], content };
  emit('update:sequence', newSeq);
};

const handleFileUpload = async (event, index) => {
  const file = event.target.files?.[0];
  if (!file) return;

  uploading.value = { ...uploading.value, [index]: true };
  try {
    const result = await uploadFile(file, props.accountId);
    const currentContent = props.sequence[index].content;
    updateMessage(index, {
      ...currentContent,
      url: result.fileUrl,
      fileName: file.name,
      blobId: result.blobId,
    });
  } catch {
    alert.show(t('CAMPAIGN.WIZARD.SEQUENCE.UPLOAD_ERROR'), 'error');
  } finally {
    uploading.value = { ...uploading.value, [index]: false };
  }
};

const insertVariable = variable => {
  if (!activeMessage.value || activeMessage.value.type !== 'text') return;
  const varText = `{{${variable}}}`;
  const current = activeMessage.value.content?.text || '';
  updateMessage(activeIndex.value, {
    ...activeMessage.value.content,
    text: current + varText,
  });
  showVariables.value = false;
  nextTick(() => {
    if (textareaRef.value?.$el) {
      const el = textareaRef.value.$el.querySelector('textarea');
      if (el) el.focus();
    }
  });
};

const getAcceptTypes = type => {
  switch (type) {
    case 'image':
      return 'image/*';
    case 'video':
      return 'video/*';
    case 'audio':
      return 'audio/*,.mp3,.ogg,.wav,.m4a';
    case 'document':
      return '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
    default:
      return '*/*';
  }
};

const getUploadHint = type => {
  switch (type) {
    case 'image':
      return 'JPG, PNG, GIF, WebP';
    case 'video':
      return 'MP4, MOV, AVI';
    case 'audio':
      return 'MP3, OGG, WAV, M4A';
    case 'document':
      return 'PDF, Word, Excel, PowerPoint';
    default:
      return '';
  }
};

const moveItem = (index, direction) => {
  const newIndex = index + direction;
  if (newIndex < 0 || newIndex >= props.sequence.length) return;
  const newSeq = [...props.sequence];
  [newSeq[index], newSeq[newIndex]] = [newSeq[newIndex], newSeq[index]];
  emit('update:sequence', newSeq);
  activeIndex.value = newIndex;
};

// Template helpers
const loadTemplates = async inboxId => {
  if (!inboxId || !props.accountId) return;
  templateLoading.value = true;
  templateInboxId.value = inboxId;
  try {
    const templates =
      store.getters['inboxes/getFilteredWhatsAppTemplates'](inboxId);
    const filterApproved = list =>
      (list || []).filter(tmpl => tmpl.status === 'approved');

    if (templates && templates.length > 0) {
      templateList.value = filterApproved(templates);
    } else {
      await store.dispatch('inboxes/syncTemplates', inboxId);
      await store.dispatch('inboxes/get');
      templateList.value = filterApproved(
        store.getters['inboxes/getFilteredWhatsAppTemplates'](inboxId)
      );
    }
  } catch {
    templateList.value = [];
  } finally {
    templateLoading.value = false;
  }
};

const selectTemplate = tmpl => {
  if (!activeMessage.value || activeMessage.value.type !== 'template') return;
  const variables = {};
  (tmpl.components || []).forEach(comp => {
    (comp.example?.body_text?.[0] || []).forEach((val, idx) => {
      variables[`{{${idx + 1}}}`] = val;
    });
  });
  updateMessage(activeIndex.value, {
    ...activeMessage.value.content,
    templateName: tmpl.name,
    templateId: tmpl.id,
    templateData: tmpl,
    variables,
  });
};

const getTemplateBody = tmpl => {
  if (!tmpl?.components) return '';
  const body = tmpl.components.find(c => c.type === 'BODY');
  return body?.text || '';
};

// ---- Variáveis do template (estilo NexTags): cada {{n}} pode ser preenchida
// com atributo do contato, texto fixo ou código único por destinatário. O
// valor vai verbatim pro backend, que interpola por contato no envio.
const VARIABLE_SOURCES = computed(() => [
  {
    token: '{{primeiro_nome}}',
    short: t('CAMPAIGN.WIZARD.SEQUENCE.VAR_FIRST_NAME'),
    sample: 'João',
  },
  {
    token: '{{contact.name}}',
    short: t('CAMPAIGN.WIZARD.SEQUENCE.VAR_NAME'),
    sample: 'João Silva',
  },
  {
    token: '{{contact.phone}}',
    short: t('CAMPAIGN.WIZARD.SEQUENCE.VAR_PHONE'),
    sample: '+55 46 99911-2233',
  },
  {
    token: '{{codigo_unico}}',
    short: t('CAMPAIGN.WIZARD.SEQUENCE.VAR_UNIQUE_CODE'),
    sample: 'X7K2Q9',
  },
]);

const templateVariableKeys = computed(() =>
  Object.keys(activeMessage.value?.content?.variables || {})
);

const updateTemplateVariable = (key, value) => {
  updateMessage(activeIndex.value, {
    ...activeMessage.value.content,
    variables: {
      ...(activeMessage.value.content.variables || {}),
      [key]: value,
    },
  });
};

const previewValue = value => {
  let out = value || '';
  const samples = {
    '{{contact.name}}': 'João Silva',
    '{{nome}}': 'João Silva',
    '{{name}}': 'João Silva',
    '{{primeiro_nome}}': 'João',
    '{{first_name}}': 'João',
    '{{contact.first_name}}': 'João',
    '{{contact.phone}}': '+55 46 99911-2233',
    '{{telefone}}': '+55 46 99911-2233',
    '{{contact.email}}': 'joao@email.com',
    '{{email}}': 'joao@email.com',
    '{{codigo_unico}}': 'X7K2Q9',
    '{{unique_code}}': 'X7K2Q9',
  };
  Object.entries(samples).forEach(([token, sample]) => {
    out = out.replaceAll(token, sample);
  });
  return out;
};

// Corpo do template com as variáveis já substituídas pro preview ao vivo.
const renderedTemplateBody = computed(() => {
  let body = getTemplateBody(activeMessage.value?.content?.templateData);
  const vars = activeMessage.value?.content?.variables || {};
  Object.entries(vars).forEach(([key, value]) => {
    body = body.replaceAll(key, previewValue(value) || key);
  });
  return body;
});

const getTemplateFooter = tmpl => {
  if (!tmpl?.components) return '';
  const footer = tmpl.components.find(c => c.type === 'FOOTER');
  return footer?.text || '';
};

const getTemplateButtons = tmpl => {
  if (!tmpl?.components) return [];
  const btns = tmpl.components.find(c => c.type === 'BUTTONS');
  return btns?.buttons || [];
};

const getTemplateHeaderImage = tmpl => {
  if (!tmpl?.components) return null;
  const header = tmpl.components.find(
    c => c.type === 'HEADER' && c.format === 'IMAGE'
  );
  if (!header) return null;
  return (
    header.example?.header_handle?.[0] ||
    header.example?.header_url?.[0] ||
    null
  );
};

const hasImageHeader = tmpl => {
  if (!tmpl?.components) return false;
  return tmpl.components.some(c => c.type === 'HEADER' && c.format === 'IMAGE');
};

const handleHeaderImageUpload = async event => {
  const file = event.target.files?.[0];
  if (!file || !activeMessage.value || activeMessage.value.type !== 'template')
    return;
  uploadingHeaderImage.value = true;
  try {
    const result = await uploadFile(file, props.accountId);
    updateMessage(activeIndex.value, {
      ...activeMessage.value.content,
      headerImageUrl: result.fileUrl,
    });
  } catch {
    alert.show(t('CAMPAIGN.WIZARD.SEQUENCE.UPLOAD_ERROR'), 'error');
  } finally {
    uploadingHeaderImage.value = false;
  }
};

// Watch for template inbox changes
watch(
  () => activeMessage.value?.content?.inboxId,
  newId => {
    if (newId && activeMessage.value?.type === 'template') {
      loadTemplates(newId);
    }
  }
);
</script>

<template>
  <div class="space-y-5">
    <!-- Add message type buttons -->
    <div class="rounded-xl border border-n-weak p-4">
      <p class="text-xs font-medium text-n-slate-10 mb-3">
        {{ t('CAMPAIGN.WIZARD.SEQUENCE.ADD_TO_SEQUENCE') }}
      </p>
      <div class="flex flex-wrap gap-2">
        <button
          v-for="msgType in availableTypes"
          :key="msgType.type"
          type="button"
          class="flex items-center gap-2 px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-3 hover:border-n-slate-6 transition-all"
          :class="{
            'border-n-brand/30 bg-n-brand/5 text-n-brand':
              msgType.type === 'ai',
          }"
          @click="addMessage(msgType.type)"
        >
          <span :class="msgType.icon" class="w-4 h-4" />
          {{ msgType.label }}
        </button>
      </div>
    </div>

    <!-- Sequence list + Editor -->
    <div
      v-if="sequence.length > 0"
      class="grid grid-cols-1 md:grid-cols-3 gap-4"
    >
      <!-- Left: Sequence list -->
      <div class="rounded-xl border border-n-weak p-4">
        <p class="text-xs font-medium text-n-slate-10 mb-3">
          {{ t('CAMPAIGN.WIZARD.SEQUENCE.SEQUENCE_LABEL') }}
          ({{ sequence.length }})
        </p>
        <div class="space-y-2 max-h-[400px] overflow-y-auto">
          <div
            v-for="(item, index) in sequence"
            :key="index"
            class="flex items-center gap-2.5 p-3 rounded-xl cursor-pointer transition-all"
            :class="
              activeIndex === index
                ? 'bg-n-brand/10 border border-n-brand/30'
                : 'bg-n-alpha-1 hover:bg-n-alpha-2 border border-transparent'
            "
            @click="activeIndex = index"
          >
            <span
              class="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium bg-n-alpha-3 text-n-slate-11"
            >
              {{ index + 1 }}
            </span>
            <span
              :class="getTypeConfig(item.type).icon"
              class="w-4 h-4 text-n-slate-10 flex-shrink-0"
            />
            <span class="flex-1 text-sm truncate text-n-slate-12">
              {{ getPreviewText(item) }}
            </span>
            <button
              v-if="index > 0"
              type="button"
              class="p-0.5 rounded text-n-slate-9 hover:text-n-slate-12 transition-colors"
              @click.stop="moveItem(index, -1)"
            >
              <span class="i-lucide-chevron-up w-3.5 h-3.5" />
            </button>
            <button
              v-if="index < sequence.length - 1"
              type="button"
              class="p-0.5 rounded text-n-slate-9 hover:text-n-slate-12 transition-colors"
              @click.stop="moveItem(index, 1)"
            >
              <span class="i-lucide-chevron-down w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              class="p-1 rounded text-n-slate-9 hover:text-n-ruby-11 hover:bg-n-ruby-3 transition-colors"
              @click.stop="removeMessage(index)"
            >
              <span class="i-lucide-x w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      <!-- Right: Editor panel -->
      <div class="md:col-span-2 rounded-xl border border-n-weak p-4">
        <template v-if="activeMessage">
          <!-- Editor header -->
          <div class="flex items-center gap-2 mb-4">
            <span
              :class="getTypeConfig(activeMessage.type).icon"
              class="w-4 h-4 text-n-slate-11"
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WIZARD.SEQUENCE.EDITING') }}:
              {{ getTypeConfig(activeMessage.type).label }}
            </span>
          </div>

          <!-- ===== TEXT editor ===== -->
          <div v-if="activeMessage.type === 'text'" class="space-y-3">
            <TextArea
              ref="textareaRef"
              :model-value="activeMessage.content.text || ''"
              :placeholder="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.MESSAGE.PLACEHOLDER')
              "
              :rows="8"
              @update:model-value="
                updateMessage(activeIndex, {
                  ...activeMessage.content,
                  text: $event,
                })
              "
            />
            <div class="flex items-center justify-end gap-2">
              <button
                type="button"
                class="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 border border-n-weak transition-colors"
                @click="showVariables = !showVariables"
              >
                <span class="i-lucide-braces w-3 h-3" />
                {{ t('CAMPAIGN.WIZARD.INSERT_VARIABLE') }}
              </button>
            </div>
            <div
              v-if="showVariables"
              class="p-3 rounded-lg bg-n-alpha-2 border border-n-weak"
            >
              <p class="text-xs text-n-slate-10 mb-2">
                {{ t('CAMPAIGN.WIZARD.SEQUENCE.CLICK_TO_INSERT') }}
              </p>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="v in VARIABLES"
                  :key="v.key"
                  type="button"
                  class="flex items-center gap-1 px-2 py-1 text-xs font-mono rounded-lg bg-n-alpha-3 text-n-slate-11 hover:bg-n-alpha-4 transition-colors"
                  @click="insertVariable(v.key)"
                >
                  <span :class="v.icon" class="w-3 h-3" />
                  {{ v.label }}
                </button>
              </div>
            </div>
          </div>

          <!-- ===== AI editor ===== -->
          <div v-if="activeMessage.type === 'ai'" class="space-y-4">
            <!-- Info banner -->
            <div
              class="flex items-start gap-2 p-3 rounded-lg bg-n-brand/5 border border-n-brand/20"
            >
              <span class="i-lucide-sparkles w-4 h-4 text-n-brand mt-0.5" />
              <p class="text-xs text-n-brand">
                {{ t('CAMPAIGN.WIZARD.SEQUENCE.AI_INFO') }}
              </p>
            </div>

            <!-- User prompt -->
            <TextArea
              :model-value="activeMessage.content.userPrompt || ''"
              :label="t('CAMPAIGN.WIZARD.SEQUENCE.AI_PROMPT_LABEL')"
              :placeholder="t('CAMPAIGN.WIZARD.SEQUENCE.AI_PROMPT_PLACEHOLDER')"
              :rows="5"
              @update:model-value="
                updateMessage(activeIndex, {
                  ...activeMessage.content,
                  userPrompt: $event,
                })
              "
            />

            <!-- Tone selection -->
            <div class="space-y-2">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('CAMPAIGN.WIZARD.SEQUENCE.AI_TONE') }}
              </label>
              <div class="grid grid-cols-2 gap-2">
                <button
                  v-for="tone in AI_TONES"
                  :key="tone.value"
                  type="button"
                  class="flex flex-col p-2.5 rounded-lg border text-left transition-all"
                  :class="
                    activeMessage.content.tone === tone.value
                      ? 'bg-n-brand/5 border-n-brand'
                      : 'bg-n-alpha-1 border-n-weak hover:border-n-slate-6'
                  "
                  @click="
                    updateMessage(activeIndex, {
                      ...activeMessage.content,
                      tone: tone.value,
                    })
                  "
                >
                  <span
                    class="text-xs font-medium"
                    :class="
                      activeMessage.content.tone === tone.value
                        ? 'text-n-brand'
                        : 'text-n-slate-12'
                    "
                  >
                    {{ tone.label }}
                  </span>
                  <span class="text-[10px] text-n-slate-10">
                    {{ tone.desc }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Toggles -->
            <div class="space-y-3">
              <div
                class="flex items-center justify-between p-2.5 rounded-lg bg-n-alpha-2"
              >
                <div class="flex items-center gap-2">
                  <span class="i-lucide-smile w-4 h-4 text-n-slate-11" />
                  <span class="text-sm text-n-slate-12">
                    {{ t('CAMPAIGN.WIZARD.SEQUENCE.AI_EMOJIS') }}
                  </span>
                </div>
                <Switch
                  :model-value="activeMessage.content.useEmojis"
                  @update:model-value="
                    updateMessage(activeIndex, {
                      ...activeMessage.content,
                      useEmojis: $event,
                    })
                  "
                />
              </div>
              <div
                class="flex items-center justify-between p-2.5 rounded-lg bg-n-alpha-2"
              >
                <div class="flex items-center gap-2">
                  <span class="i-lucide-hand-metal w-4 h-4 text-n-slate-11" />
                  <span class="text-sm text-n-slate-12">
                    {{ t('CAMPAIGN.WIZARD.SEQUENCE.AI_GREETING') }}
                  </span>
                </div>
                <Switch
                  :model-value="activeMessage.content.includeGreeting"
                  @update:model-value="
                    updateMessage(activeIndex, {
                      ...activeMessage.content,
                      includeGreeting: $event,
                    })
                  "
                />
              </div>
            </div>

            <!-- Max length -->
            <div class="space-y-2">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('CAMPAIGN.WIZARD.SEQUENCE.AI_MAX_LENGTH') }}
              </label>
              <div class="flex items-center gap-3">
                <input
                  type="range"
                  min="100"
                  max="1000"
                  step="50"
                  :value="activeMessage.content.maxLength || 500"
                  class="flex-1 accent-n-brand"
                  @input="
                    updateMessage(activeIndex, {
                      ...activeMessage.content,
                      maxLength: parseInt($event.target.value),
                    })
                  "
                />
                <span class="text-sm font-mono text-n-slate-12 w-14 text-right">
                  {{ `${activeMessage.content.maxLength || 500}` }}
                </span>
              </div>
            </div>

            <!-- Advanced: system prompt -->
            <button
              type="button"
              class="flex items-center gap-1 text-xs text-n-slate-10 hover:text-n-slate-12 transition-colors"
              @click="showAdvancedAi = !showAdvancedAi"
            >
              <span
                class="w-3 h-3"
                :class="
                  showAdvancedAi
                    ? 'i-lucide-chevron-down'
                    : 'i-lucide-chevron-right'
                "
              />
              {{ t('CAMPAIGN.WIZARD.SEQUENCE.AI_ADVANCED') }}
            </button>
            <div v-if="showAdvancedAi">
              <TextArea
                :model-value="activeMessage.content.systemPrompt || ''"
                :label="t('CAMPAIGN.WIZARD.SEQUENCE.AI_SYSTEM_PROMPT')"
                :rows="4"
                @update:model-value="
                  updateMessage(activeIndex, {
                    ...activeMessage.content,
                    systemPrompt: $event,
                  })
                "
              />
            </div>
          </div>

          <!-- ===== TEMPLATE editor ===== -->
          <div v-if="activeMessage.type === 'template'" class="space-y-4">
            <!-- Inbox selector -->
            <div class="space-y-2">
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('CAMPAIGN.WIZARD.SEQUENCE.TEMPLATE_INBOX') }}
              </label>
              <div class="grid grid-cols-1 gap-2">
                <button
                  v-for="inbox in whatsappInboxes"
                  :key="inbox.id"
                  type="button"
                  class="flex items-center gap-2 p-2.5 rounded-lg border text-left transition-all"
                  :class="
                    activeMessage.content.inboxId === inbox.id
                      ? 'border-n-brand bg-n-brand/5'
                      : 'border-n-weak hover:border-n-slate-6'
                  "
                  @click="
                    updateMessage(activeIndex, {
                      ...activeMessage.content,
                      inboxId: inbox.id,
                    })
                  "
                >
                  <span class="i-lucide-inbox w-4 h-4 text-n-slate-11" />
                  <span class="text-sm text-n-slate-12">
                    {{ inbox.name }}
                  </span>
                </button>
              </div>
            </div>

            <!-- Template list -->
            <div v-if="activeMessage.content.inboxId" class="space-y-2">
              <div class="flex items-center justify-between">
                <label class="text-xs font-medium text-n-slate-11">
                  {{ t('CAMPAIGN.WIZARD.SEQUENCE.TEMPLATE_SELECT') }}
                </label>
                <span
                  v-if="templateLoading"
                  class="i-lucide-loader-2 w-4 h-4 text-n-brand animate-spin"
                />
              </div>

              <div
                v-if="templateList.length > 0"
                class="space-y-2 max-h-[250px] overflow-y-auto"
              >
                <button
                  v-for="tmpl in templateList"
                  :key="tmpl.name"
                  type="button"
                  class="w-full flex flex-col p-3 rounded-lg border text-left transition-all"
                  :class="
                    activeMessage.content.templateName === tmpl.name
                      ? 'border-n-brand bg-n-brand/5'
                      : 'border-n-weak hover:border-n-slate-6'
                  "
                  @click="selectTemplate(tmpl)"
                >
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ tmpl.name }}
                  </span>
                  <span class="text-xs text-n-slate-10 mt-1 line-clamp-2">
                    {{ getTemplateBody(tmpl) }}
                  </span>
                  <div
                    v-if="tmpl.language || tmpl.status"
                    class="flex items-center gap-2 mt-1.5"
                  >
                    <span
                      v-if="tmpl.status"
                      class="text-[10px] px-1.5 py-0.5 rounded font-medium bg-n-teal-3 text-n-teal-11"
                    >
                      {{ tmpl.status }}
                    </span>
                    <span
                      v-if="tmpl.language"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-3 text-n-slate-10"
                    >
                      {{ tmpl.language }}
                    </span>
                    <span
                      v-if="tmpl.category"
                      class="text-[10px] px-1.5 py-0.5 rounded bg-n-alpha-3 text-n-slate-10"
                    >
                      {{ tmpl.category }}
                    </span>
                  </div>
                </button>
              </div>

              <div
                v-else-if="!templateLoading"
                class="text-center py-6 text-n-slate-10"
              >
                <span
                  class="i-lucide-layout-template w-8 h-8 block mx-auto mb-2 opacity-40"
                />
                <p class="text-xs">
                  {{ t('CAMPAIGN.WIZARD.SEQUENCE.TEMPLATE_EMPTY') }}
                </p>
              </div>
            </div>

            <!-- Selected template preview -->
            <div
              v-if="activeMessage.content.templateData"
              class="rounded-lg bg-n-alpha-2 border border-n-weak overflow-hidden"
            >
              <p class="text-xs font-medium text-n-slate-12 px-3 pt-3 mb-2">
                {{ t('CAMPAIGN.WIZARD.SEQUENCE.TEMPLATE_PREVIEW') }}
              </p>

              <!-- Header image -->
              <div
                v-if="hasImageHeader(activeMessage.content.templateData)"
                class="px-3"
              >
                <img
                  v-if="
                    activeMessage.content.headerImageUrl ||
                    getTemplateHeaderImage(activeMessage.content.templateData)
                  "
                  :src="
                    activeMessage.content.headerImageUrl ||
                    getTemplateHeaderImage(activeMessage.content.templateData)
                  "
                  class="w-full h-40 object-cover rounded-lg mb-2"
                  alt="Header"
                />
                <div
                  v-else
                  class="w-full h-40 rounded-lg mb-2 bg-n-alpha-3 flex items-center justify-center"
                >
                  <span class="i-lucide-image w-8 h-8 text-n-slate-8" />
                </div>
              </div>

              <!-- Body (com variáveis substituídas — preview ao vivo) -->
              <p class="text-sm text-n-slate-11 whitespace-pre-wrap px-3">
                {{ renderedTemplateBody }}
              </p>

              <!-- Footer -->
              <p
                v-if="getTemplateFooter(activeMessage.content.templateData)"
                class="text-xs text-n-slate-9 mt-1 px-3"
              >
                {{ getTemplateFooter(activeMessage.content.templateData) }}
              </p>

              <!-- Buttons -->
              <div
                v-if="
                  getTemplateButtons(activeMessage.content.templateData)
                    .length > 0
                "
                class="mt-2 border-t border-n-weak"
              >
                <div
                  v-for="(btn, idx) in getTemplateButtons(
                    activeMessage.content.templateData
                  )"
                  :key="idx"
                  class="text-center py-2 text-sm text-n-blue-11 font-medium border-b border-n-weak last:border-b-0"
                >
                  {{ btn.text }}
                </div>
              </div>
            </div>

            <!-- Variáveis do template: contato / texto fixo / código único -->
            <div v-if="templateVariableKeys.length" class="space-y-2">
              <p class="text-xs font-medium text-n-slate-12">
                {{ t('CAMPAIGN.WIZARD.SEQUENCE.TEMPLATE_VARIABLES') }}
              </p>
              <div
                v-for="key in templateVariableKeys"
                :key="key"
                class="flex items-center gap-2"
              >
                <span class="text-xs font-mono text-n-slate-10 w-10 shrink-0">
                  {{ key }}
                </span>
                <input
                  :value="activeMessage.content.variables[key]"
                  class="flex-1 min-w-0 px-2 py-1.5 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
                  :placeholder="t('CAMPAIGN.WIZARD.SEQUENCE.VARIABLE_VALUE_PH')"
                  @input="updateTemplateVariable(key, $event.target.value)"
                />
                <div class="flex gap-1 shrink-0">
                  <button
                    v-for="src in VARIABLE_SOURCES"
                    :key="src.token"
                    type="button"
                    class="px-1.5 py-1 text-[10px] rounded-md bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 whitespace-nowrap"
                    :title="src.token"
                    @click="updateTemplateVariable(key, src.token)"
                  >
                    {{ src.short }}
                  </button>
                </div>
              </div>
              <p class="text-[11px] text-n-slate-10">
                {{ t('CAMPAIGN.WIZARD.SEQUENCE.VARIABLES_HINT') }}
              </p>
            </div>

            <!-- Header image upload -->
            <div
              v-if="
                activeMessage.content.templateData &&
                hasImageHeader(activeMessage.content.templateData)
              "
              class="space-y-2"
            >
              <label class="text-xs font-medium text-n-slate-11">
                {{ t('CAMPAIGN.WIZARD.SEQUENCE.TEMPLATE_IMAGE_URL') }}
              </label>
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  class="relative flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 transition-colors"
                  :disabled="uploadingHeaderImage"
                >
                  <span
                    v-if="uploadingHeaderImage"
                    class="i-lucide-loader-2 w-4 h-4 animate-spin"
                  />
                  <span v-else class="i-lucide-upload w-4 h-4" />
                  {{
                    activeMessage.content.headerImageUrl
                      ? t('CAMPAIGN.WIZARD.SEQUENCE.TEMPLATE_CHANGE_IMAGE')
                      : t('CAMPAIGN.WIZARD.SEQUENCE.TEMPLATE_UPLOAD_IMAGE')
                  }}
                  <input
                    type="file"
                    accept="image/*"
                    class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    :disabled="uploadingHeaderImage"
                    @change="handleHeaderImageUpload"
                  />
                </button>
              </div>
              <input
                type="url"
                :value="activeMessage.content.headerImageUrl || ''"
                :placeholder="
                  getTemplateHeaderImage(activeMessage.content.templateData) ||
                  'https://example.com/image.jpg'
                "
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 placeholder:text-n-slate-8 focus:outline-none focus:border-n-brand"
                @input="
                  updateMessage(activeIndex, {
                    ...activeMessage.content,
                    headerImageUrl: $event.target.value,
                  })
                "
              />
              <p class="text-xs text-n-slate-9">
                {{ t('CAMPAIGN.WIZARD.SEQUENCE.TEMPLATE_IMAGE_HINT') }}
              </p>
            </div>
          </div>

          <!-- ===== IMAGE / VIDEO editor ===== -->
          <div
            v-if="
              activeMessage.type === 'image' || activeMessage.type === 'video'
            "
            class="space-y-4"
          >
            <div>
              <label class="text-xs font-medium text-n-slate-11 mb-2 block">
                {{
                  activeMessage.type === 'image'
                    ? t('CAMPAIGN.WIZARD.SEQUENCE.TYPES.IMAGE')
                    : t('CAMPAIGN.WIZARD.SEQUENCE.TYPES.VIDEO')
                }}
              </label>
              <div
                class="border-2 border-dashed border-n-weak rounded-xl p-8 text-center hover:border-n-slate-6 transition-colors cursor-pointer relative"
              >
                <input
                  type="file"
                  :accept="getAcceptTypes(activeMessage.type)"
                  class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  @change="handleFileUpload($event, activeIndex)"
                />
                <div
                  v-if="uploading[activeIndex]"
                  class="flex flex-col items-center"
                >
                  <span
                    class="i-lucide-loader-2 w-8 h-8 text-n-brand animate-spin"
                  />
                  <p class="mt-2 text-sm text-n-slate-10">
                    {{ t('CAMPAIGN.WIZARD.SEQUENCE.UPLOADING') }}
                  </p>
                </div>
                <div
                  v-else-if="activeMessage.content.url"
                  class="flex flex-col items-center"
                >
                  <img
                    v-if="activeMessage.type === 'image'"
                    :src="activeMessage.content.url"
                    class="w-32 h-32 object-cover rounded-lg"
                    alt="Preview"
                  />
                  <video
                    v-else
                    :src="activeMessage.content.url"
                    class="w-32 h-32 object-cover rounded-lg"
                  />
                  <p class="mt-2 text-xs text-n-slate-10 font-medium">
                    {{ activeMessage.content.fileName }}
                  </p>
                  <p class="text-xs text-n-slate-9">
                    {{ t('CAMPAIGN.WIZARD.SEQUENCE.CLICK_TO_CHANGE') }}
                  </p>
                </div>
                <div v-else class="flex flex-col items-center">
                  <span
                    class="i-lucide-upload-cloud w-10 h-10 text-n-slate-8"
                  />
                  <p class="mt-2 text-sm text-n-slate-10">
                    {{ t('CAMPAIGN.WIZARD.SEQUENCE.CLICK_TO_UPLOAD') }}
                  </p>
                  <p class="text-xs text-n-slate-9 mt-1">
                    {{ getUploadHint(activeMessage.type) }}
                  </p>
                </div>
              </div>
            </div>
            <TextArea
              :model-value="activeMessage.content.caption || ''"
              :label="t('CAMPAIGN.WIZARD.SEQUENCE.CAPTION')"
              :placeholder="t('CAMPAIGN.WIZARD.SEQUENCE.CAPTION_PLACEHOLDER')"
              :rows="3"
              @update:model-value="
                updateMessage(activeIndex, {
                  ...activeMessage.content,
                  caption: $event,
                })
              "
            />
          </div>

          <!-- ===== AUDIO editor ===== -->
          <div v-if="activeMessage.type === 'audio'" class="space-y-4">
            <div
              class="border-2 border-dashed border-n-weak rounded-xl p-8 text-center hover:border-n-slate-6 transition-colors cursor-pointer relative"
            >
              <input
                type="file"
                :accept="getAcceptTypes('audio')"
                class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                @change="handleFileUpload($event, activeIndex)"
              />
              <div
                v-if="uploading[activeIndex]"
                class="flex flex-col items-center"
              >
                <span
                  class="i-lucide-loader-2 w-8 h-8 text-n-brand animate-spin"
                />
                <p class="mt-2 text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.SEQUENCE.UPLOADING') }}
                </p>
              </div>
              <div
                v-else-if="activeMessage.content.url"
                class="flex flex-col items-center"
              >
                <span class="i-lucide-mic w-10 h-10 text-n-teal-11" />
                <p class="mt-2 text-sm font-medium text-n-slate-12">
                  {{ activeMessage.content.fileName }}
                </p>
                <audio
                  :src="activeMessage.content.url"
                  controls
                  class="mt-2 max-w-full"
                />
                <p class="text-xs text-n-slate-9 mt-2">
                  {{ t('CAMPAIGN.WIZARD.SEQUENCE.CLICK_TO_CHANGE') }}
                </p>
              </div>
              <div v-else class="flex flex-col items-center">
                <span class="i-lucide-mic w-10 h-10 text-n-slate-8" />
                <p class="mt-2 text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.SEQUENCE.CLICK_TO_UPLOAD') }}
                </p>
                <p class="text-xs text-n-slate-9 mt-1">
                  {{ getUploadHint('audio') }}
                </p>
              </div>
            </div>
            <p class="text-xs text-n-slate-10">
              {{ t('CAMPAIGN.WIZARD.SEQUENCE.AUDIO_HINT') }}
            </p>
          </div>

          <!-- ===== DOCUMENT editor ===== -->
          <div v-if="activeMessage.type === 'document'" class="space-y-4">
            <div
              class="border-2 border-dashed border-n-weak rounded-xl p-8 text-center hover:border-n-slate-6 transition-colors cursor-pointer relative"
            >
              <input
                type="file"
                :accept="getAcceptTypes('document')"
                class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                @change="handleFileUpload($event, activeIndex)"
              />
              <div
                v-if="uploading[activeIndex]"
                class="flex flex-col items-center"
              >
                <span
                  class="i-lucide-loader-2 w-8 h-8 text-n-brand animate-spin"
                />
                <p class="mt-2 text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.SEQUENCE.UPLOADING') }}
                </p>
              </div>
              <div
                v-else-if="activeMessage.content.url"
                class="flex flex-col items-center"
              >
                <span class="i-lucide-file-text w-10 h-10 text-n-blue-11" />
                <p class="mt-2 text-sm font-medium text-n-slate-12">
                  {{ activeMessage.content.fileName }}
                </p>
                <p class="text-xs text-n-slate-9 mt-1">
                  {{ t('CAMPAIGN.WIZARD.SEQUENCE.CLICK_TO_CHANGE') }}
                </p>
              </div>
              <div v-else class="flex flex-col items-center">
                <span class="i-lucide-file-text w-10 h-10 text-n-slate-8" />
                <p class="mt-2 text-sm text-n-slate-10">
                  {{ t('CAMPAIGN.WIZARD.SEQUENCE.CLICK_TO_UPLOAD') }}
                </p>
                <p class="text-xs text-n-slate-9 mt-1">
                  {{ getUploadHint('document') }}
                </p>
              </div>
            </div>
          </div>

          <!-- ===== WAIT editor ===== -->
          <div v-if="activeMessage.type === 'wait'" class="space-y-3">
            <label class="text-xs font-medium text-n-slate-11 block">
              {{ t('CAMPAIGN.WIZARD.SEQUENCE.WAIT_TIME') }}
            </label>
            <div class="flex items-center gap-4">
              <input
                type="range"
                min="5"
                max="300"
                :value="activeMessage.content.waitTime || 30"
                class="flex-1 accent-n-brand"
                @input="
                  updateMessage(activeIndex, {
                    waitTime: parseInt($event.target.value),
                  })
                "
              />
              <span class="text-lg font-medium text-n-slate-12 w-12 text-right">
                {{ `${activeMessage.content.waitTime || 30}s` }}
              </span>
            </div>
            <p class="text-xs text-n-slate-10">
              {{ t('CAMPAIGN.WIZARD.SEQUENCE.WAIT_HINT') }}
            </p>
          </div>
        </template>

        <!-- Empty editor state -->
        <div
          v-else
          class="flex flex-col items-center justify-center py-12 text-center"
        >
          <span class="i-lucide-mouse-pointer-click w-10 h-10 text-n-slate-8" />
          <p class="mt-3 text-sm text-n-slate-10">
            {{ t('CAMPAIGN.WIZARD.SEQUENCE.SELECT_TO_EDIT') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Empty state - no messages yet -->
    <div
      v-if="sequence.length === 0"
      class="flex flex-col items-center justify-center py-12 text-center rounded-xl border-2 border-dashed border-n-weak"
    >
      <span class="i-lucide-message-square-plus w-12 h-12 text-n-slate-8" />
      <p class="mt-3 text-sm text-n-slate-11">
        {{ t('CAMPAIGN.WIZARD.SEQUENCE.EMPTY') }}
      </p>
      <p class="text-xs text-n-slate-9 mt-1">
        {{ t('CAMPAIGN.WIZARD.SEQUENCE.EMPTY_HINT') }}
      </p>
    </div>
  </div>
</template>
